import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router";
import { Button } from "@/components/ui/Button";
import H1 from "@/components/ui/H1";
import P from "@/components/ui/P";
import { CheckCircle2, LayoutDashboard } from "lucide-react";
import PartyImg from "@/res/images/party-illustration.png";
import LoadingSection from "@/components/LoadingSection";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/Alert";
import { AlertCircle } from "lucide-react";
import { ManualPaymentService } from "@/api/manualPaymentService";
import { getBuyPanelsPath } from "../route";
import { doFetch } from "@/util/fetch";
import { getIdToken } from "@/util/authentication";

export default function ManualPaymentSuccess() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const requestId = searchParams.get("requestId");
    const paymentIntentId = searchParams.get("payment_intent");
    const token = searchParams.get("token");

    const [isConfirming, setIsConfirming] = useState(true);
    const [confirmError, setConfirmError] = useState<string | null>(null);
    const routePrefix = getBuyPanelsPath(window.location.pathname, "");

    useEffect(() => {
        let cancelled = false;

        if (!sessionId && !paymentIntentId && requestId) {
            setIsConfirming(false);
            return () => {
                cancelled = true;
            };
        }

        if (!sessionId && (!requestId || !paymentIntentId)) {
            setIsConfirming(false);
            return () => {
                cancelled = true;
            };
        }

        const confirm = async () => {
            if (sessionId) {
                return ManualPaymentService.confirmSession(sessionId);
            }

            const query = new URLSearchParams();
            query.set("paymentIntentId", paymentIntentId!);
            if (token) {
                query.set("token", token);
            }

            const headers: Record<string, string> = {
                Accept: "application/json",
            };

            if (!token) {
                const idToken = await getIdToken();
                if (idToken) {
                    headers.Authorization = `Bearer ${idToken.replace(/\"/g, "")}`;
                }
            }

            const response = await doFetch(
                `/api/orders/payment/manual/${encodeURIComponent(requestId!)}/stripe/complete?${query.toString()}`,
                {
                    method: "POST",
                    headers,
                }
            );

            if (!response.ok) {
                const content = await (async () => {
                    try {
                        return await response.json();
                    } catch {
                        return await response.text();
                    }
                })();

                throw new Error(typeof content === "object" ? content.message || JSON.stringify(content) : String(content));
            }

            return await response.json();
        };

        confirm()
            .then((response) => {
                if (!cancelled) {
                    if ((response as { confirmed?: boolean }).confirmed === false) {
                        setConfirmError("Payment is still processing. We will update the order as soon as Stripe confirms it.");
                    }
                    setIsConfirming(false);
                }
            })
            .catch((error) => {
                console.error("Failed to confirm manual payment session", error);
                if (!cancelled) {
                    setConfirmError(error.message || error.body?.message || "Payment completed, but the backend confirmation is still syncing.");
                    setIsConfirming(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [paymentIntentId, requestId, sessionId, token]);

    if (isConfirming) {
        return <LoadingSection />;
    }

    return (
        <div className="mx-auto max-w-screen-sm p-6 py-20 text-center space-y-8">
            {confirmError ? (
                <Alert variant="destructive" className="text-left">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Confirmation pending</AlertTitle>
                    <AlertDescription>{confirmError}</AlertDescription>
                </Alert>
            ) : null}
            <div className="flex flex-col items-center space-y-4">
                <CheckCircle2 className="h-20 w-20 text-green-500 animate-in zoom-in duration-500" />
                <H1 className="text-brand-orange-500 font-bold">Payment Successful!</H1>
                <P className="text-lg">
                    Thank you! Your balance payment has been processed successfully.
                </P>
            </div>

            <div className="bg-brand-neutral-800 p-8 rounded-2xl border border-brand-neutral-700 shadow-xl relative overflow-hidden group">
                <div className="relative z-10 space-y-4">
                    <p className="text-muted-foreground">
                        Your panels are now being allocated and will appear in your dashboard shortly. 
                        You'll receive a formal receipt via email soon.
                    </p>
                    <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
                        <Button asChild className="bg-brand-orange-500 hover:bg-brand-orange-600 px-8">
                            <Link to="/dashboard" className="flex items-center gap-2">
                                <LayoutDashboard className="h-4 w-4" />
                                Go to Dashboard
                            </Link>
                        </Button>
                        <Button variant="outline" asChild className="px-8 border-brand-neutral-600">
                            <Link to={routePrefix}>Buy More Panels</Link>
                        </Button>
                    </div>
                </div>
                
                <img 
                    src={PartyImg} 
                    alt="Success" 
                    className="absolute -bottom-10 -right-10 w-48 opacity-20 group-hover:opacity-30 transition-opacity"
                />
            </div>

            <p className="text-sm text-muted-foreground pt-10">
                If you have any questions, please contact our support team at <br/>
                <a href="mailto:support@solarcloud.com.au" className="text-brand-orange-500 hover:underline">support@solarcloud.com.au</a>
            </p>
        </div>
    );
}
